/**
 * The tabs of an employee profile a reader is offered, and which tab opens
 * when the URL names one they may not.
 *
 * Assets, Access, Notes and History read data behind a permission of their
 * own: Assets (`assets.view`), Access (`access.view`), Notes
 * (`useCanViewEmployeeNotes`, HW-1053) and History (`employees.audit.view`,
 * HW-795). The tab bar (`buildProfileTabs`) and the `?tab=` fallback on the
 * profile page (`resolveProfileTab`) both ask `canOpenProfileTab`, so they agree
 * on these four — a tab hidden from the bar
 * but mounted from a bookmarked link renders a 403 as an empty list, and a tab
 * offered by the bar but forced to Job is a button that does nothing. [HW-940]
 *
 * Documents opens for everyone who can open the card: it holds the FOP payment
 * details and Education blocks, and each block — like the file tree, which keeps
 * the `useCanViewEmployeeDocuments` gate (HW-785) — hides itself from a reader
 * without its own permission. [HW-1008]
 *
 * The other gated tabs (Absences, Emergency, Compensations) are hidden
 * from the bar on flags `ProfileCard` passes in, but `?tab=` still opens them;
 * `canOpenProfileTab` does not decide them.
 *
 * Kept out of the components because the repo's vitest runs in a node
 * environment, where a component cannot be rendered — same reasoning as
 * `features/time-tracker/lib/time-tabs.ts`.
 */

/** Which of Assets, Access, Notes and History this reader may open on this employee's profile. */
export interface ProfileTabAccess {
  /**
   * `useCanViewEmployeeDocuments(employeeId)` — own, team or global documents read.
   * No longer gates the Documents tab, only its file tree and document-count badge. [HW-1008]
   */
  canViewDocuments: boolean;
  /** `assets.view` — what `GET /assets/employee/:employeeId` requires. */
  canViewAssets: boolean;
  /** `access.view` — what `GET /access-grants/employee/:employeeId` requires. */
  canViewAccess: boolean;
  /** `useCanViewEmployeeNotes(employeeId)` — what `GET /employees/:id/notes` admits. [HW-1053] */
  canViewNotes: boolean;
  /** `employees.audit.view` — what `GET /employees/:id/audit-log` requires. [HW-795] */
  canViewHistory: boolean;
}

/** Every tab of the profile, in the order the bar shows them. */
export const PROFILE_TAB_IDS = [
  'job',
  'emergency',
  'documents',
  'assets',
  'access',
  'notes',
  'absences',
  'compensations',
  'history',
] as const;

export type ProfileTabId = (typeof PROFILE_TAB_IDS)[number];

/** Whether a URL's `?tab=` names a tab at all — spelling only; `resolveProfileTab` decides access. */
export function isProfileTabId(value: unknown): value is ProfileTabId {
  return typeof value === 'string' && (PROFILE_TAB_IDS as readonly string[]).includes(value);
}

/** The tabs `canOpenProfileTab` decides. */
export type GatedProfileTab = 'assets' | 'access' | 'notes' | 'history';

const GATES: Record<GatedProfileTab, (access: ProfileTabAccess) => boolean> = {
  assets: (a) => a.canViewAssets,
  access: (a) => a.canViewAccess,
  notes: (a) => a.canViewNotes,
  history: (a) => a.canViewHistory,
};

/** Whether the reader may open `tab`. Tabs this module does not decide pass. */
export function canOpenProfileTab(tab: string, access: ProfileTabAccess): boolean {
  return Object.hasOwn(GATES, tab) ? GATES[tab as GatedProfileTab](access) : true;
}

/**
 * The tab that actually opens: the one asked for, or Job when the reader may
 * not open it. `?tab=` mounts a tab straight from the URL, past the tab bar, so
 * a link pasted between colleagues whose roles differ must not land on a tab
 * whose data the reader is refused.
 */
export function resolveProfileTab<T extends string>(
  requested: T,
  access: ProfileTabAccess
): T | 'job' {
  return canOpenProfileTab(requested, access) ? requested : 'job';
}

export interface ProfileTab {
  id: ProfileTabId;
  label: string;
  badge?: number;
}

/** What the bar needs beyond `ProfileTabAccess`: the flags `ProfileCard` resolves and the badge counts. */
export interface ProfileTabOptions {
  access: ProfileTabAccess;
  showEmergency: boolean;
  showAbsences: boolean;
  showCompensations: boolean;
  documentCount?: number;
  assetCount?: number;
  accessCount?: number;
}

const badge = (count: number | undefined) => (count && count > 0 ? count : undefined);

/** The tabs this reader is offered, in the order the bar shows them. */
export function buildProfileTabs(opts: ProfileTabOptions): ProfileTab[] {
  const tabs: ProfileTab[] = [{ id: 'job', label: 'Job' }];
  if (opts.showEmergency) tabs.push({ id: 'emergency', label: 'Emergency' });
  tabs.push({ id: 'documents', label: 'Documents', badge: badge(opts.documentCount) });
  if (canOpenProfileTab('assets', opts.access)) {
    tabs.push({ id: 'assets', label: 'Assets', badge: badge(opts.assetCount) });
  }
  if (canOpenProfileTab('access', opts.access)) {
    tabs.push({ id: 'access', label: 'Access', badge: badge(opts.accessCount) });
  }
  if (canOpenProfileTab('notes', opts.access)) tabs.push({ id: 'notes', label: 'Notes' });
  if (opts.showAbsences) tabs.push({ id: 'absences', label: 'Absences' });
  if (opts.showCompensations) tabs.push({ id: 'compensations', label: 'Compensations' });
  if (canOpenProfileTab('history', opts.access)) tabs.push({ id: 'history', label: 'History' });
  return tabs;
}
